import { BehaviorSubject, timer, Observable, Subject, combineLatest } from 'rxjs';
import { FeatureCollection, Point, Geometry, Feature } from 'geojson';
import { MapElement } from '../map-element.model';
import { map, publishReplay, takeUntil, filter } from 'rxjs/operators';
import mapboxgl, { GeoJSONSource } from 'mapbox-gl';
import { TranslateService } from '@ngx-translate/core';
import { BaseLayer } from './base-layer.model';
import { createMapboxLayer, createFillLayer, createLineLayer, createSymbolLayer, getClickedPointForFeature, applyFilter,
  searchFromFeatures as searchFromFeatureList, createCircleLayer } from './utils/map.utils';
import { feature, featureCollection, point } from '@turf/turf';
import { ILayer, LayerStyle, LayerMetadata, LayerFilter, ILayerFunctionality, MapboxLayerType,
  MapboxEventListener, LayerSearchOptions, LayerSearchResult, StandardLayer } from './layer.type';
import { IRegoFeatureDefinition } from '~app/linking/mapping';
import { TagPair } from '~app/shared/map/layer/layer-definitions/T14';
import { MapSettingsService } from '../components/map-settings/map-settings-service';
import { FlagService } from '~app/shared/flag/flag.service';

export type Layer = HTMLMarkerLayer | CanvasLayer;

export interface FeatureFromMapbox {
  id: string | number,
  layer: string,
  properties: any,
  type: string,
  geometry: any,
}

export interface ClickData {
  clickedFeature: FeatureFromMapbox,
  event: {
    type: 'click' | 'dblclick',
    originalEvent: {
      clientX: number,
      clientY: number
    }
    utilId?: string,
  }
}

export class HTMLMarkerLayer extends BaseLayer implements ILayer, ILayerFunctionality {
  title?: string;
  style?: { layerType?: 'html-marker'; sourceType?: 'geojson'; } & LayerStyle;
  asMarkers?: true;
  source?: string;
  selectable?: boolean;
  data?: MapElement<Point>[];
  metadata: LayerMetadata;
  searchOptions: LayerSearchOptions;
  minZoom?: number;
  useWorker?: true;
  filter?: LayerFilter;
  geojsonURL: string;
  geojsonPostprocess?: (data: FeatureCollection<Point>) => FeatureCollection<Point>;
  orderingWeight?: number;

  protected readonly mapRef: mapboxgl.Map;
  /**
   * Emit data as markers$ for html-marker -layers
   */
  private readonly featuresBS = new BehaviorSubject<MapElement<Point>[]>([]);
  public readonly features$ = this.featuresBS.asObservable();

  constructor(
    mapRef: mapboxgl.Map,
    layerDefinition: ILayer,
    mapSettingService: MapSettingsService
  ) {
    super(mapSettingService);
    this.mapRef = mapRef;
    // TODO: Try typing
    // Create Layer from layerDefinition
    for (const key in layerDefinition) {
      this[key] = layerDefinition[key];
    }
  }

  public getFeatures() {
    return this.featuresBS.getValue();
  }

  // Split this to initGeojsonLayer / initVectorLayer. Possibly add type guards for same method?
  public async initLayer(geojson: FeatureCollection<Geometry>) {
    // Verify that layer isn't already added on the map
    const layerFoundOnMap: mapboxgl.Layer = this.mapRef.getLayer(this.id);
    if (!layerFoundOnMap) {
      this.addHTMLMarkersFromGeoJson(geojson);
    }
  }

  async addHTMLMarkersFromGeoJson(geojson: FeatureCollection) {
    try {
      if (geojson) {
        const markers: MapElement<Point>[] = geojson.features.map(feature =>
          new MapElement<Point>(
            feature.properties.id,
            this,
            feature.properties,
            feature.type,
            feature.geometry));
        this.pushFeatures(markers);
      }
    } catch (e) {
      console.error(e);
    }
  }

  public pushFeatures(data: MapElement<Point>[]) {
    this.setLoadedStatus(true);
    this.featuresBS.next(data);
  }

  public pushGeojson(data: FeatureCollection<Point>) {
    const source = this.mapRef.getSource(this.id);
    if (source?.type === 'geojson') {
      if (this.geojsonPostprocess) {
        data = this.geojsonPostprocess(data);
      }
      source.setData(data);
      const mapElements: MapElement<Point>[] = data.features.map(f => new MapElement<Point>(f.id, this, f.properties, f.type, f.geometry));
      this.featuresBS.next(mapElements);
    } else {
      console.error('Trying to push geojson to', this.id, 'layer');
    }
  }

  public setLoadedStatus(isLoaded: boolean) {
    this.isLoadedBS.next(isLoaded);
  }

  public searchFromFeatures(searchTerm: string, mappings?: IRegoFeatureDefinition[]): LayerSearchResult[] {
    return searchFromFeatureList(searchTerm, this.searchOptions, this.featuresBS.getValue(), mappings);
  }

  public getSingleFeature$(featureId: string): Observable<MapElement<Point>> {
    return this.features$.pipe(
      map(features => features.find(f => f.id === featureId)),
      publishReplay(1)
    );
  }

  public getSingleFeature(featureId: string | number): MapElement<Point> {
    // weak comparison since element.id is potentially a number
    // tslint:disable-next-line
    return this.featuresBS.getValue().find(feature => feature.id == featureId);
  }
}

export class CanvasLayer extends BaseLayer implements ILayer, ILayerFunctionality {
  title?: string;
  style?: { layerType?: MapboxLayerType; } & LayerStyle;
  asMarkers?: false;
  source?: string;
  selectable?: boolean;
  linkable?: boolean;
  animated?: boolean;
  linkedTextTags?: TagPair[];
  data?: MapElement<Geometry>[];
  metadata: LayerMetadata;
  searchOptions: LayerSearchOptions;
  minZoom?: number;
  useWorker?: true;
  filter?: LayerFilter;
  geojsonURL?: string;
  geojsonPostprocess?: (data: FeatureCollection<Geometry>) => FeatureCollection<Geometry>;
  vectorURL?: string;
  vectorSourceLayer?: string;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date;
  styleFunction?: any;
  orderingWeight?: number;
  belowRoadLayers?: boolean;
  belowTitles?: boolean;
  fromExternal?: boolean;
  mapboxOptions?: {
    layout?: mapboxgl.AnyLayout;
    paint?: mapboxgl.AnyPaint;
  };

  protected readonly mapRef: mapboxgl.Map;

  private readonly utilityLayers: { id: string, utilId: string, layerDefinition: ILayer }[] = [];

  private readonly featuresBS = new BehaviorSubject<MapElement<Geometry>[]>([]);
  public readonly features$ = this.featuresBS.asObservable();

  private readonly featureClicked = new Subject<ClickData>();
  public readonly featureClicked$ = this.featureClicked.asObservable();

  private readonly isHoveredBS = new BehaviorSubject<boolean>(false);
  public readonly isHovered$ = this.isHoveredBS.asObservable();

  private eventHandlers: MapboxEventListener[] = [];
  private popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false
  });

  constructor(
    mapRef: mapboxgl.Map,
    layerDefinition: ILayer,
    private translate: TranslateService,
    private mapSettingService: MapSettingsService,
    private flagService: FlagService
  ) {
    super(mapSettingService);
    this.mapRef = mapRef;
    // Create Layer from layerDefinition
    for (const key in layerDefinition) {
      this[key] = layerDefinition[key];
    }

    const isStyleLoaded = new Subject<boolean>();
    timer(0, 1000).pipe(
      map(d => this.mapRef.isStyleLoaded()),
      filter(loaded => loaded),
      takeUntil(isStyleLoaded)
    ).subscribe(data => {
      if (data) {
        console.log('layer style and source IS loaded', this.id, data);
        isStyleLoaded.next(true);
      }
    });

    //// if layer is not visible, 'flagfilter' is on and required loads have finished
    //// --> 'flip' the layer and its group as visible and selected,
    combineLatest([mapSettingService.flagLayerFilter$, this.flagService.flags$, this.isLoaded$, isStyleLoaded]).pipe(
      filter(([flagFilter, flags, layerLoaded, styleLoaded ]) => !this.getIsVisible() && flagFilter.flagFilterEnabled && layerLoaded && styleLoaded),
      filter(([flagFilter, flags, layerLoaded, styleLoaded ]) => flags.some(er => er.layerId === this.id))
    ).subscribe(all => {
      console.log('set visibility and selected status for layer ', this.id);
      this.setVisibility(true, true);
    });

    //// set layer opacity according 'flagfilter'
    //// TODO's
    //// ---> perhaps could cache map state just before "toggle-on" and restore the state after "toggle-off"
    combineLatest([this.isVisible$, mapSettingService.flagLayerFilter$, this.flagService.flags$, this.isLoaded$, isStyleLoaded]).pipe(
      filter(([layerVisible, flagFilter, flags, layerLoaded, styleLoaded ]) => layerVisible.layerVisible && layerLoaded && styleLoaded)
    ).subscribe(([layerVisible, flagFilter, flags, layerLoaded, styleLoaded ]) => {
      const opaque = 1.0;
      const transparent = 0.2;
      if (flagFilter.flagFilterEnabled) {
        this.setVectorLayerOpacity(transparent);
        for (const feat of this.getFeatures()) {
          //// comparing feature id's as strings (it's number or string)...
          if (flags.some(flag => flag.targetObjectId === feat.id.toString())) {
            this.setGeojsonFeatureOpacity(feat.id, opaque);
          } else {
            this.setGeojsonFeatureOpacity(feat.id, transparent);
          }
        }
      } else { /// flagFilter inactive
        this.setVectorLayerOpacity(opaque);
        for (const feat of this.getFeatures()) {
          this.setGeojsonFeatureOpacity(feat.id, opaque);
        }
      }
    });
  }

  private setVectorLayerOpacity(opac: number) {
    if (this.style?.sourceType === 'vector' ) {
      const key = this.style?.layerType==='symbol'?'icon-opacity':`${this.style?.layerType}-opacity`;
      console.log('changing layer opacity ',this.style?.sourceType, this.id, key, opac[key]);
      this.mapRef.setPaintProperty(this.id, key, opac);
    }
  }

  private setGeojsonFeatureOpacity(featId: any, opacitySettings: number) {
    if (featId && this.style?.sourceType === 'geojson') {
      this.mapRef.setFeatureState({
        id: featId,
        source: this.id,
        //...(this.vectorSourceLayer && {sourceLayer: this.vectorSourceLayer}),
      }, {
        'opacity': opacitySettings
      });
    }
  }

  public getFeatures() {
    return this.featuresBS.getValue();
  }

  // Layer init -->
  // Split this to initGeojsonLayer / initVectorLayer. Possibly add type guards for same method?
  public async initLayer(beforeLayerId: string, geojson: FeatureCollection) {
    this.clearEventListeners();

    const layerFoundOnMap: mapboxgl.AnyLayer = this.mapRef.getLayer(this.id);
    if (!layerFoundOnMap) { // Verify that layer isn't already added on the map
      if (['line', 'symbol', 'fill', 'circle'].includes(this.style.layerType)) {
        let mapboxLayer: StandardLayer = null;
        switch (this.style.sourceType) {
          case 'geojson':
            mapboxLayer = await this.createGeojsonLayer(this, geojson);
            break;
          case 'vector':
            try {
              mapboxLayer = await this.createVectorLayer(this.vectorURL);
            } catch (err) {
              console.error(err);
            }
            break;
          default:
            console.log('Source type not defined for', this.id);
        }

        this.setLoadedStatus(true);

        mapboxLayer.minzoom = this.metadata?.visibleByZoom || 0;

        //console.log('Add layer', mapboxLayer);
        this.mapRef.addLayer(mapboxLayer, beforeLayerId);

        // Set filters for geojson data
        if (this.filter) {
          this.mapRef.setFilter(this.id, [
            this.filter.mode === 'OR' ? 'any' : 'all',
            ...this.filter.filters.map(filter => [
              filter.mode === 'exclusive' ? '!in' : 'in',
              filter.field,
              ...(Array.isArray(filter.value) ? filter.value : [filter.value]),
            ])
          ]);
        }

      } else {
        console.log('Layer for websocket data');
      }
    }

    // utility layers may have been removed during a style change
    for (const { utilId, layerDefinition } of this.utilityLayers) {
      await this.addUtilityLayer(utilId, layerDefinition);
    }

    // Click handler for the layer (used to open hover)
    // Add a new event listener only when the layer is loaded first time.
    // Mapbox does not remove layer event listeners when style is changed and layers are reloaded.
    // if (layer.metadata.hoverComponent && layer.isLoaded.getValue()) {
    if (this.metadata.hoverComponent || this.metadata.delegateClick) {
      this.bindMouseEvents(this.id);

      // for URL description hovers
      if (this.metadata.url && this.metadata.urlDescription) {
        this.addMapEventListener({ event: 'mouseenter', layerId: this.id, listener: (e) => this.urlPopupShow(e) });
        this.addMapEventListener({ event: 'mouseleave', layerId: this.id, listener: () => this.urlPopupHide() });
      }
    }
  }
  // <-- Layer init

  private bindMouseEvents(layerId: string, utilId?: string) {
    this.addMapEventListener({ event: 'click', layerId, listener: this.onLayerClick.bind(this, utilId) });
    this.addMapEventListener({ event: 'dblclick', layerId, listener: this.onLayerClick.bind(this, utilId) });
    // Change the cursor to a pointer when the mouse is over the layer.
    this.addMapEventListener({ event: 'mouseenter', layerId, listener: this.onMouseEnterFeature.bind(this, utilId) });
    // Change it back to a pointer when it leaves.
    this.addMapEventListener({ event: 'mouseleave', layerId, listener: this.onMouseLeaveFeature.bind(this, utilId) });
    // Disable context menu
    this.addMapEventListener({ event: 'contextmenu', layerId, listener: this.onContextMenu.bind(this, utilId) });
  }

  urlPopupShow(e: any) {
    const coordinates = e.features[0].geometry.coordinates.slice();
    const description = this.translate.instant(this.metadata.urlDescription);

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    this.popup.setLngLat(coordinates).setHTML(description).addTo(this.mapRef);
  }

  urlPopupHide() {
    this.popup.remove();
  }

  addMapEventListener(ev: MapboxEventListener) {
    this.mapRef.on(ev.event, ev.layerId, ev.listener);
    this.eventHandlers.push(ev);
  }

  clearEventListeners() {
    while (this.eventHandlers.length > 0) {
      const ev = this.eventHandlers.pop();
      this.mapRef.off(ev.event, ev.layerId, ev.listener);
    }
  }

  onMouseEnterFeature(utilId?: string) {
    this.isHoveredBS.next(true);
  }

  onMouseLeaveFeature(utilId?: string) {
    this.isHoveredBS.next(false);
  }

  onContextMenu(utilId: string | undefined, event: mapboxgl.MapMouseEvent) {
    event.preventDefault();
  }

  /**
   * Handler for layer click and dblclick events
   */
  private onLayerClick(utilId: string | undefined, e: mapboxgl.MapMouseEvent) {
    e.preventDefault();
    e.originalEvent.stopPropagation();

    const resolveClick = ([feature, ...restFeatures]: mapboxgl.MapboxGeoJSONFeature[]) => {
      // Get top layer of the matched stack
      if (!feature || feature.source === 'composite' || (!this.metadata.hoverComponent && !this.metadata.delegateClick)) { // End handler if empty or not custom layer
        if (restFeatures.length > 0) {
          resolveClick(restFeatures);
        }
        return;
      } else {
        if (this.metadata.url) {
          window.open(this.metadata.url + feature.properties[this.metadata.urlProperty], 'blank');
        } else {
          const clickedPoint = point([e.lngLat.lng, e.lngLat.lat]);
          const geometry = getClickedPointForFeature(feature.geometry, clickedPoint);

          const featureId = feature.properties[feature.properties.uniqueBy]
            || feature.id || `${feature.layer.id} (${geometry.coordinates[0].toPrecision(6)}, ${geometry.coordinates[1].toPrecision(6)})`;
          const featureProps = { ...feature.properties, [feature.properties.uniqueBy]: featureId };
          const clickedFeature: FeatureFromMapbox = {
            ...new MapElement<Point>(featureId, null, featureProps, '', geometry),
            layer: feature.layer.id
          };
          console.log('Click original feature:', feature, 'clicked point:', clickedFeature);
          this.featureClicked.next({
            clickedFeature,
            event: {
              // e.type is always 'click' or 'dbclick' because this function is only bound to those events
              type: e.type as ('click' | 'dblclick'),
              originalEvent: {
                clientX: e.point.x,
                clientY: e.point.y,
              },
              utilId,
            }
          });
        }
      }
    };

    const features = this.mapRef.queryRenderedFeatures(e.point);
    resolveClick(features);
  }

  /**
   * Creates a layer with vector source
   */
  private async createVectorLayer(vectorURL: string): Promise<StandardLayer> {
    if (!this.isSourceAdded(this.id)) {
      if (vectorURL) {
        try {
          this.createVectorSource(vectorURL);
        } catch (err) {
          console.error(err);
        }
      } else {
        throw new Error('No source url for ' + this.id);
      }
    }

    let mapboxLayer: StandardLayer;
    // Layer type specific setup
    switch (this.style.layerType) {
      case 'symbol': // Load images for symbol layers
        const imageId = await this.addImage(this);
        mapboxLayer = createSymbolLayer(this, imageId);
        break;
      case 'line':
        mapboxLayer = createLineLayer(this);
        break;
      case 'fill':
          mapboxLayer = createFillLayer(this);
          break;
      case 'circle':
        mapboxLayer = createCircleLayer(this);
        break;
      default:
        console.error('Unknown layer type', this.style.layerType);
        mapboxLayer = createMapboxLayer(this) as any;
    }
    mapboxLayer['source-layer'] = this.vectorSourceLayer;
    return mapboxLayer;
  }

  /**
   * Creates a layer with geojson source
   */
  private async createGeojsonLayer(layerDefinition: ILayer, geojson?: FeatureCollection):
    Promise<StandardLayer>
  {
    /**
     * Add to observable to make the data accessible (for search etc.)
     */
    if (geojson) {
      this.featuresBS.next(applyFilter(layerDefinition.filter, geojson.features.map(feature =>
        new MapElement<Point>(
          feature.id ? feature.id : feature.properties[layerDefinition.metadata.uniqueBy],
          this,
          feature.properties,
          feature.type,
          feature.geometry)
      )));
    }

    const sourceData: mapboxgl.GeoJSONSourceOptions = {};
    if (layerDefinition.metadata?.attribution) {
      sourceData.attribution = layerDefinition.metadata.attribution;
    }
    if (layerDefinition.metadata?.uniqueBy) {
      sourceData.promoteId = layerDefinition.metadata.uniqueBy;
    }

    if (layerDefinition.geojsonURL) {
      this.createGeojsonSource(layerDefinition, geojson, sourceData);
    } else { // Add geojson source with no initial data
      const emptyGeojson: FeatureCollection<Geometry> = {
        type: 'FeatureCollection',
        features: []
      };
      this.createGeojsonSource(layerDefinition, emptyGeojson, sourceData);
    }

    // Layer type specific setup
    switch (layerDefinition.style.layerType) {
      case 'symbol': // Load images for symbol layers
        const imageId = await this.addImage(layerDefinition);
        return createSymbolLayer(layerDefinition, imageId);
      case 'line':
        return createLineLayer(layerDefinition);
      case 'fill':
        return createFillLayer(layerDefinition);
      case 'circle':
        return createCircleLayer(layerDefinition);
      default:
        console.error('Unknown layer type', layerDefinition.style.layerType);
        return createMapboxLayer(layerDefinition) as any;
    }
  }

  private async createGeojsonSource(
    layerDefinition: ILayer,
    geojsonData: FeatureCollection,
    sourceData: mapboxgl.GeoJSONSourceOptions,
  ) {
    if (!this.isSourceAdded(layerDefinition.id)) {
      this.mapRef.addSource(layerDefinition.id, {
        type: 'geojson',
        data: geojsonData,
        ...sourceData
      });
    }
  }

  /**
   * Creates an internal utility layer for extra styling or data.
   * @param utilId unique (within the scope of this layer) ID for the new utility layer
   * @returns a Subject that can be used to change the data of the layer
   */
  public async addUtilityLayer(utilId: string, layerDefinition: Partial<ILayer>): Promise<Subject<Feature[]>> {
    const id = `${this.id}-util-${utilId}`;

    if (!this.mapRef.getLayer(id)) {
      const completeDefinition: ILayer = {
        id,
        name: id,
        selectable: false,
        ...layerDefinition,
        style: {
          clickable: false,
          sourceType: 'geojson',
          visible: this.style.visible,
          ...layerDefinition.style,
        },
        metadata: {
          uniqueBy: 'id',
          labelBy: 'id',
          ...layerDefinition.metadata,
        },
        searchOptions: {
          searchable: false,
          ...layerDefinition.searchOptions,
        },
      };

      // store data so that the layer can be re-initialized on style change
      if (!this.utilityLayers.some(util => util.id === id)) {
        this.utilityLayers.push({ id, utilId, layerDefinition: completeDefinition });
      }

      const layer = await this.createGeojsonLayer(completeDefinition);
      layer.minzoom = this.metadata?.visibleByZoom || 0;

      console.log('Add utility layer', layer);
      this.mapRef.addLayer(layer, this.getLowestSubLayer());

      this.isVisible$.subscribe((isVisible) => {
        this.mapRef.setLayoutProperty(id, 'visibility', isVisible ? 'visible' : 'none');
      });

      if (completeDefinition.style.clickable) {
        this.bindMouseEvents(id, utilId);
      }
    }

    const subject = new Subject<Feature[]>();
    subject.subscribe(features => {
      const source = this.mapRef.getSource(id) as GeoJSONSource;
      source.setData({
        type: 'FeatureCollection',
        features,
      });
    });
    return subject;
  }

  public getUtilityLayerDefinitionByFullId(id: string): ILayer | undefined {
    return this.utilityLayers.find(layer => layer.id === id)?.layerDefinition;
  }

  /**
   * Returns the ID of the lowest utility layer, if one exists.
   * Otherwise returns the layer's own ID.
   */
  public getLowestSubLayer(): string {
    for (let i = this.utilityLayers.length - 1; i >= 0; --i) {
      const id = this.utilityLayers[i].id;
      if (this.mapRef.getLayer(id)) {
        return id;
      }
    }
    return this.id;
  }

  private async addImage(layerDefinition: ILayer): Promise<string> {
    if (!layerDefinition.style.icon?.url) {
      return undefined;
    }
    return await new Promise((resolve, reject) => {
      this.mapRef.loadImage(
        layerDefinition.style.icon?.url,
        (error, image) => {
          if (error) {
            reject(error);
          } else if (!this.mapRef.hasImage(layerDefinition.id)) {
            this.mapRef.addImage(layerDefinition.id, image, { sdf: !!layerDefinition.style.icon?.colorable });
          }
          resolve(layerDefinition.id);
        }
      );
    });
  }

  private createVectorSource(vectorURL: string) {
    const vectorSource: mapboxgl.VectorSource = {
      type: 'vector',
      url: vectorURL
    };
    if (this.metadata?.attribution) {
      vectorSource.attribution = this.metadata.attribution;
    }
    this.mapRef.addSource(this.id, {
      type: 'vector',
      url: vectorSource.url,
      ...vectorSource
    });
  }

  private isSourceAdded(sourceId: string): boolean {
    return !!this.mapRef.getSource(sourceId);
  }

  async addHTMLMarkersFromGeoJson(geojson: FeatureCollection) {
    try {
      if (geojson) {
        const markers: MapElement<Point>[] = geojson.features.map(feature =>
          new MapElement<Point>(
            feature.properties[this.metadata.uniqueBy],
            this,
            feature.properties,
            feature.type,
            feature.geometry));
        this.pushFeatures(markers);
      }
    } catch (e) {
      console.error(e);
    }
  }

  public isPointLayer(): boolean {
    return this.style.layerType === 'symbol' || this.style.layerType === 'circle';
  }

  public setFeatureProperties(data: {
    [id: string]: { [prop: string]: string | number },
  }): void {
    // check due to limitations of `pushFeatures`
    if (!this.isPointLayer()) {
      console.error(`Called setFeatureProperties on ${this.style.layerType} layer ${this.id}`);
      return;
    }
    const features = this.getFeatures() as MapElement<Point>[];

    // to avoid unnecessary map updates when nothing has actually changed
    let hasChanges = false;

    for (const [id, properties] of Object.entries(data)) {
      const feature = features.find(feature => feature.id.toString() === id);
      if (feature) {
        // loop instead of `Object.assign` so that `hasChanges` can be updated
        for (const [key, value] of Object.entries(properties)) {
          if (feature.properties[key] !== value) {
            feature.properties[key] = value;
            hasChanges = true;
          }
        }
      }
    }

    if (hasChanges) {
      this.pushFeatures(features);
    }
  }

  public pushFeatures(data: MapElement<Point>[]) {
    const source = this.mapRef.getSource(this.id);
    if (source?.type === 'geojson') {
      const features = data.map(me => {
        const featureProps = {
          ...me.properties,
          layer: this.id,
          uniqueBy: this.metadata.uniqueBy
        };
        return feature(me.geometry, featureProps)
      });
      const geojson = featureCollection(features);
      try {
        source.setData(geojson as FeatureCollection);
      } catch (err) {
        console.error(err);
        console.warn(geojson, features);
      }
      this.featuresBS.next(applyFilter(this.filter, data));
    } else {
      console.error('Trying to push geojson to', this.id, 'layer with type', source?.type);
    }
  }

  public pushGeojson(data: FeatureCollection<Geometry>) {
    const source = this.mapRef.getSource(this.id);
    if (source?.type === 'geojson') {
      if (this.geojsonPostprocess) {
        data = this.geojsonPostprocess(data);
      }
      source.setData(data);
      const mapElements = data.features.map(f => new MapElement(f.id, this, f.properties, f.type, f.geometry));
      this.featuresBS.next(applyFilter(this.filter, mapElements));
    } else {
      console.error('Trying to push geojson to', this.id, 'layer. Source:', source);
    }
  }

  public setLoadedStatus(isLoaded: boolean) {
    this.isLoadedBS.next(isLoaded);
  }

  public searchFromFeatures(searchTerm: string, mappings?: IRegoFeatureDefinition[]): LayerSearchResult[] {
    return searchFromFeatureList(searchTerm, this.searchOptions, this.featuresBS.getValue(), mappings);
  }

  public getSingleFeature$(featureId: string): Observable<MapElement<Geometry>> {
    return this.features$.pipe(
      map(features => features.find(f => f.id === featureId)),
      publishReplay(1)
    );
  }

  public getSingleFeature(featureId: string | number): MapElement<Geometry> {
    return this.featuresBS.getValue().find(f => {
      if (f.layer?.metadata?.uniqueBy) {
        return (f.properties[f.layer.metadata.uniqueBy] === featureId);
      } else {
        return f.id === featureId;
      }
    });
  }

  public moveBelow(layer: CanvasLayer) {
    this.mapRef.moveLayer(this.id, layer.getLowestSubLayer());
    for (let i = 0; i < this.utilityLayers.length; ++i) {
      this.mapRef.moveLayer(layer.id, this.utilityLayers[i - 1]?.id ?? this.id);
    }
  }

}
